/**
 * Platform Admin Tools Tab
 *
 * Allows platform admins (super_admin) to manage the public tool catalog:
 * - CRUD for public (platform-wide) tools
 * - Toggle featured status
 * - View platform-wide stats
 * - Manage tool categories
 */

import { useState, useCallback, useMemo } from "react";
import {
  usePlatformAdminTools,
  usePlatformAdminToolStats,
  useCreatePlatformTool,
  useUpdatePlatformTool,
  useDeletePlatformTool,
  useToggleToolFeatured,
  useToolCategories,
} from "@hooks/usePlatformAdminTools";
import {
  DataTable,
  type Column,
  Modal,
  SearchInput,
  StatusBadge,
} from "@components/admin";
import { Button } from "@components/common";
import { showToast } from "@stores/uiStore";
import type {
  PlatformTool,
  PlatformToolFilters,
  PlatformToolCreate,
} from "@/types/adminTools";

type SortDirection = "asc" | "desc";

export function PlatformToolsTab() {
  // Filters & pagination
  const [search, setSearch] = useState("");
  const [toolTypeFilter, setToolTypeFilter] = useState<
    "all" | "public" | "private"
  >("all");
  const [featuredFilter, setFeaturedFilter] = useState<string>("");
  const [topicFilter, setTopicFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>("title");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const pageSize = 20;

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toolToEdit, setToolToEdit] = useState<PlatformTool | null>(null);
  const [toolToDelete, setToolToDelete] = useState<PlatformTool | null>(null);
  const [toolToFeature, setToolToFeature] = useState<PlatformTool | null>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState<PlatformToolCreate>({
    title: "",
    description: "",
    externalUrl: "",
    topics: [],
    difficulty: "beginner",
    author: "",
    featured: false,
  });

  // Build filters object
  const filters: PlatformToolFilters = useMemo(
    () => ({
      search: search || undefined,
      toolType: toolTypeFilter === "all" ? undefined : toolTypeFilter,
      featured: featuredFilter === "" ? undefined : featuredFilter === "true",
      topic: topicFilter || undefined,
      page,
      pageSize,
    }),
    [search, toolTypeFilter, featuredFilter, topicFilter, page, pageSize],
  );

  // Queries & mutations
  const {
    data: toolsData,
    isLoading,
    refetch,
  } = usePlatformAdminTools(filters);
  const { data: stats } = usePlatformAdminToolStats();
  const { data: categories = [] } = useToolCategories();
  const createTool = useCreatePlatformTool();
  const updateTool = useUpdatePlatformTool();
  const deleteTool = useDeletePlatformTool();
  const toggleFeatured = useToggleToolFeatured();

  // Sort tools client-side
  const tools = toolsData?.tools;
  const sortedTools = useMemo(() => {
    if (!tools) return [];
    return [...tools].sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortColumn];
      const bVal = (b as unknown as Record<string, unknown>)[sortColumn];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [tools, sortColumn, sortDirection]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn],
  );

  // Handle create tool
  const handleCreateTool = async () => {
    try {
      await createTool.mutateAsync(formData);
      showToast(`Tool "${formData.title}" created successfully`, "success");
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      showToast(
        `Failed to create tool: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
    }
  };

  // Handle update tool
  const handleUpdateTool = async () => {
    if (!toolToEdit) return;

    try {
      await updateTool.mutateAsync({
        toolId: toolToEdit.id,
        data: formData,
      });
      showToast(`Tool "${formData.title}" updated successfully`, "success");
      setToolToEdit(null);
      resetForm();
    } catch (error) {
      showToast(
        `Failed to update tool: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
    }
  };

  // Handle delete tool
  const handleDeleteTool = async () => {
    if (!toolToDelete) return;

    try {
      await deleteTool.mutateAsync(toolToDelete.id);
      showToast(`Tool "${toolToDelete.title}" deleted successfully`, "success");
      setToolToDelete(null);
    } catch (error) {
      showToast(
        `Failed to delete tool: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
    }
  };

  // Handle toggle featured
  const handleToggleFeatured = async () => {
    if (!toolToFeature) return;

    try {
      await toggleFeatured.mutateAsync({
        toolId: toolToFeature.id,
        data: { featured: !toolToFeature.featured },
      });
      showToast(
        `Tool "${toolToFeature.title}" ${toolToFeature.featured ? "unfeatured" : "featured"} successfully`,
        "success",
      );
      setToolToFeature(null);
    } catch (error) {
      showToast(
        `Failed to update tool: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      externalUrl: "",
      topics: [],
      difficulty: "beginner",
      author: "",
      featured: false,
    });
  };

  // Open edit modal
  const openEditModal = (tool: PlatformTool) => {
    setFormData({
      title: tool.title,
      description: tool.description,
      externalUrl: tool.externalUrl || "",
      topics: tool.topics,
      difficulty: tool.difficulty,
      author: tool.author || "",
      featured: tool.featured,
    });
    setToolToEdit(tool);
  };

  // Table columns
  const columns: Column<PlatformTool>[] = useMemo(
    () => [
      {
        key: "title",
        header: "Tool",
        sortable: true,
        render: (tool) => (
          <div>
            <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
              {tool.featured && (
                <svg
                  className="w-4 h-4 text-amber-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              )}
              {tool.title}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
              {tool.description}
            </div>
          </div>
        ),
      },
      {
        key: "tenantId",
        header: "Type",
        sortable: true,
        width: "100px",
        render: (tool) => (
          <StatusBadge
            label={tool.tenantId ? "Private" : "Public"}
            variant={tool.tenantId ? "warning" : "info"}
          />
        ),
      },
      {
        key: "topics",
        header: "Category",
        width: "150px",
        render: (tool) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {tool.topics.length > 0 ? tool.topics[0] : "-"}
          </span>
        ),
      },
      {
        key: "totalBookmarks",
        header: "Bookmarks",
        sortable: true,
        width: "100px",
        render: (tool) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {tool.totalBookmarks}
          </span>
        ),
      },
      {
        key: "hiddenByTenants",
        header: "Hidden By",
        sortable: true,
        width: "100px",
        render: (tool) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {tool.hiddenByTenants} orgs
          </span>
        ),
      },
      {
        key: "difficulty",
        header: "Difficulty",
        sortable: true,
        width: "120px",
        render: (tool) => (
          <span className="text-sm capitalize text-gray-700 dark:text-gray-300">
            {tool.difficulty}
          </span>
        ),
      },
      {
        key: "actions",
        header: "",
        width: "180px",
        render: (tool) => (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setToolToFeature(tool)}
              className={`p-1.5 transition-colors ${
                tool.featured
                  ? "text-amber-500 hover:text-amber-600"
                  : "text-gray-400 hover:text-amber-500"
              }`}
              title={tool.featured ? "Remove from featured" : "Add to featured"}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
            <button
              onClick={() => openEditModal(tool)}
              className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
              title="Edit"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              onClick={() => setToolToDelete(tool)}
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      {/* Header with Stats */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Platform Tools Management
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage the public tool catalog for all organizations
          </p>
        </div>
        <div className="flex items-center gap-6">
          {stats && (
            <div className="flex gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalTools}
                </div>
                <div className="text-gray-500 dark:text-gray-400">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                  {stats.featuredTools}
                </div>
                <div className="text-gray-500 dark:text-gray-400">Featured</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {stats.totalBookmarks}
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  Bookmarks
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats.activeTenants}
                </div>
                <div className="text-gray-500 dark:text-gray-400">Tenants</div>
              </div>
            </div>
          )}
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + Add Tool
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search tools..."
          className="w-64"
        />

        <select
          value={toolTypeFilter}
          onChange={(e) => {
            setToolTypeFilter(e.target.value as "all" | "public" | "private");
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="all">All Types</option>
          <option value="public">Public Only</option>
          <option value="private">Private Only</option>
        </select>

        <select
          value={featuredFilter}
          onChange={(e) => {
            setFeaturedFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="">All Featured</option>
          <option value="true">Featured Only</option>
          <option value="false">Non-Featured Only</option>
        </select>

        <select
          value={topicFilter}
          onChange={(e) => {
            setTopicFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.slug} value={cat.slug}>
              {cat.label} ({cat.toolCount})
            </option>
          ))}
        </select>

        <button
          onClick={() => refetch()}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Refresh"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {/* Data Table */}
      <DataTable
        data={sortedTools}
        columns={columns}
        keyExtractor={(tool) => tool.id}
        isLoading={isLoading}
        emptyMessage="No tools found"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      {/* Pagination */}
      {toolsData && toolsData.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, toolsData.total)} of {toolsData.total}{" "}
            tools
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
              Page {page} of {toolsData.totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setPage((p) => Math.min(toolsData.totalPages, p + 1))
              }
              disabled={page === toolsData.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create Tool Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          title="Add Public Tool"
          size="lg"
          footer={
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateTool}
                isLoading={createTool.isPending}
                disabled={!formData.title || !formData.description}
              >
                Create Tool
              </Button>
            </div>
          }
        >
          <ToolForm
            formData={formData}
            setFormData={setFormData}
            categories={categories}
          />
        </Modal>
      )}

      {/* Edit Tool Modal */}
      {toolToEdit && (
        <Modal
          isOpen={!!toolToEdit}
          onClose={() => {
            setToolToEdit(null);
            resetForm();
          }}
          title="Edit Tool"
          size="lg"
          footer={
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setToolToEdit(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleUpdateTool}
                isLoading={updateTool.isPending}
                disabled={!formData.title || !formData.description}
              >
                Save Changes
              </Button>
            </div>
          }
        >
          <ToolForm
            formData={formData}
            setFormData={setFormData}
            categories={categories}
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {toolToDelete && (
        <Modal
          isOpen={!!toolToDelete}
          onClose={() => setToolToDelete(null)}
          title="Delete Tool"
          description={toolToDelete.title}
          size="md"
          footer={
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setToolToDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteTool}
                isLoading={deleteTool.isPending}
              >
                Delete Tool
              </Button>
            </div>
          }
        >
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete this tool? This action cannot be
            undone. All tenant approvals for this tool will also be deleted.
          </p>
        </Modal>
      )}

      {/* Toggle Featured Modal */}
      {toolToFeature && (
        <Modal
          isOpen={!!toolToFeature}
          onClose={() => setToolToFeature(null)}
          title={
            toolToFeature.featured ? "Remove from Featured" : "Add to Featured"
          }
          description={toolToFeature.title}
          size="md"
          footer={
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setToolToFeature(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleToggleFeatured}
                isLoading={toggleFeatured.isPending}
              >
                {toolToFeature.featured
                  ? "Remove from Featured"
                  : "Add to Featured"}
              </Button>
            </div>
          }
        >
          <p className="text-gray-600 dark:text-gray-400">
            {toolToFeature.featured
              ? "This tool will no longer appear in the featured section of the Tools Marketplace."
              : "This tool will be highlighted in the featured section of the Tools Marketplace."}
          </p>
        </Modal>
      )}
    </div>
  );
}

// Tool Form Component
function ToolForm({
  formData,
  setFormData,
  categories,
}: {
  formData: PlatformToolCreate;
  setFormData: React.Dispatch<React.SetStateAction<PlatformToolCreate>>;
  categories: { slug: string; label: string }[];
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Title *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, title: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="e.g., LangChain Documentation"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, description: e.target.value }))
          }
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Describe what this tool does..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          External URL
        </label>
        <input
          type="url"
          value={formData.externalUrl || ""}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, externalUrl: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="https://..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category
          </label>
          <select
            value={formData.topics?.[0] || ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                topics: e.target.value ? [e.target.value] : [],
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">Select category...</option>
            {categories.map((cat) => (
              <option key={cat.slug} value={cat.slug}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Difficulty
          </label>
          <select
            value={formData.difficulty}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                difficulty: e.target.value as PlatformToolCreate["difficulty"],
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Author
        </label>
        <input
          type="text"
          value={formData.author || ""}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, author: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="e.g., LangChain Team"
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="featured"
          checked={formData.featured}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, featured: e.target.checked }))
          }
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <label
          htmlFor="featured"
          className="ml-2 text-sm text-gray-700 dark:text-gray-300"
        >
          Featured (shown prominently in Tools Marketplace)
        </label>
      </div>
    </div>
  );
}
